class AdhyaHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-adhya-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-adhya-dot]'));
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(this.dataset.interval || 6500);
    this.isVisible = true;

    this.onVisibilityChange = () => {
      if (document.hidden) this.stop();
      else this.start();
    };

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.start();
          else this.stop();
        },
        { rootMargin: '120px 0px' }
      );
      this.visibilityObserver.observe(this);
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.querySelector('[data-adhya-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    this.querySelector('[data-adhya-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    this.scheduleDeferredImages();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.deferredImageTimers?.forEach((timer) => window.clearTimeout(timer));
    this.visibilityObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  loadSlideImage(index) {
    const slide = this.slides[index];
    const template = slide?.querySelector('template[data-adhya-image-template]');
    if (!template) return;
    slide.prepend(template.content.cloneNode(true));
    template.remove();
  }

  scheduleDeferredImages() {
    const loadRemaining = () => {
      this.deferredImageTimers = [
        window.setTimeout(() => this.loadSlideImage(1), 4000),
        window.setTimeout(() => this.loadSlideImage(2), 8000),
      ];
    };

    if (document.readyState === 'complete') loadRemaining();
    else window.addEventListener('load', loadRemaining, { once: true });
  }

  show(nextIndex, restart = false) {
    if (this.slides.length < 2) return;
    this.index = (nextIndex + this.slides.length) % this.slides.length;
    this.loadSlideImage(this.index);
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
    if (restart) {
      this.stop();
      this.start();
    }
  }

  start() {
    if (
      this.slides.length < 2 ||
      !this.isVisible ||
      document.hidden ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;
    this.stop();
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('adhya-hero')) {
  customElements.define('adhya-hero', AdhyaHero);
}

function syncCustomPrintingLinks(root = document) {
  const customUrl = document.body?.dataset.adhyaCustomUrl;
  if (!customUrl) return;

  const links = root.matches?.('header') ? root.querySelectorAll('a[href]') : root.querySelectorAll('header a[href]');
  links.forEach((link) => {
    const label = link.textContent.replace(/\s+/g, ' ').trim();
    const path = new URL(link.href, window.location.origin).pathname;
    if (
      (label === 'Custom' || label === 'Custom Apparel') &&
      (path === '/pages/contact' || path === '/pages/custom-printing' || path === '/collections/custom-apparel')
    ) {
      link.href = customUrl;
    }
  });
}

syncCustomPrintingLinks();
document.addEventListener('shopify:section:load', (event) => syncCustomPrintingLinks(event.target));

document.querySelectorAll('header').forEach((adhyaHeader) => {
  let syncQueued = false;
  new MutationObserver(() => {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncCustomPrintingLinks(adhyaHeader);
      syncQueued = false;
    });
  }).observe(adhyaHeader, {
    childList: true,
    subtree: true,
  });
});

document.querySelectorAll('.adhya-marquee').forEach((marquee) => {
  marquee.dataset.motionReady = 'true';
  if (!('IntersectionObserver' in window)) {
    marquee.classList.add('is-in-view');
    return;
  }

  const observer = new IntersectionObserver(([entry]) => {
    marquee.classList.toggle('is-in-view', entry.isIntersecting && !document.hidden);
  });
  observer.observe(marquee);
});

document.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest?.('header a[href]');
    const customUrl = document.body?.dataset.adhyaCustomUrl;
    if (!link || !customUrl) return;

    const label = link.textContent.replace(/\s+/g, ' ').trim();
    const path = new URL(link.href, window.location.origin).pathname;
    if (
      (label !== 'Custom' && label !== 'Custom Apparel') ||
      (path !== '/pages/contact' && path !== '/pages/custom-printing' && path !== '/collections/custom-apparel')
    ) {
      return;
    }

    event.preventDefault();
    window.location.assign(customUrl);
  },
  true
);
